import { useState } from 'react';
// @mui
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import Iconify from 'src/components/iconify';
import { studentDisplayName } from 'src/utils/student-name';

// ----------------------------------------------------------------------

// The bar's fixed height, one row at every breakpoint: a 44px button plus 6px
// above and below. The dashboard layout reserves exactly this much at the
// bottom of the page while impersonating, so nothing sits under the bar.
export const IMPERSONATION_BANNER_HEIGHT = 56;

// Shown on every dashboard page while a super admin views as a student (#605).
export default function ImpersonationBanner() {
  const { user, impersonatedBy, stopImpersonation } = useAuthContext();
  const [returning, setReturning] = useState(false);

  if (!impersonatedBy) return null;

  const studentName = studentDisplayName(
    user?.name || [user?.firstname, user?.lastname].filter(Boolean).join(' '),
    user?.asn,
    'student'
  );

  const handleReturn = async () => {
    setReturning(true);
    try {
      await stopImpersonation();
    } finally {
      setReturning(false);
    }
  };

  return (
    <Stack
      role="status"
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={1.5}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        height: IMPERSONATION_BANNER_HEIGHT,
        px: 2,
        // Above the page and app bar, below dialogs, so a full-screen dialog's actions stay usable
        zIndex: (theme) => theme.zIndex.appBar + 1,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        boxShadow: (theme) => theme.customShadows?.z8,
      }}
    >
      <Iconify icon="solar:eye-bold" width={20} sx={{ flexShrink: 0 }} />
      {/* Shrinks and truncates first, so the button always stays whole */}
      <Typography variant="subtitle2" noWrap title={`Viewing as ${studentName}`} sx={{ minWidth: 0 }}>
        Viewing as {studentName}
      </Typography>
      <Button
        variant="contained"
        color="inherit"
        disabled={returning}
        onClick={handleReturn}
        sx={{ flexShrink: 0, minHeight: 44 }}
      >
        Return to my account
      </Button>
    </Stack>
  );
}
